import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Alert, Button, Card, CardContent } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { post } from '../../../helpers/api.helper';
import config from '../../../config/config';
import { useVendorStore } from '../../../store/vendorStore';
import { useLoginFormDialogStore } from '../../../store/loginFormDialogStore';

interface BillingCallbackResponse {
    ok: boolean;
    bid?: string;
    cardMasked?: string;
    cardName?: string;
    message: string;
}

export function BillingReturnHandler() {
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('처리 중...');
    const [cardInfo, setCardInfo] = useState<{ masked?: string; name?: string }>({});
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { vendorId } = useVendorStore();
    const { setIsOpen: openLoginDialog } = useLoginFormDialogStore((state) => state.action);

    useEffect(() => {
        const processReturn = async () => {
            try {
                // 비회원 차단: 로그인 필요
                if (!vendorId) {
                    throw new Error('로그인이 필요합니다.');
                }
                // URL 파라미터를 모두 수집
                const payload: Record<string, string> = {};
                searchParams.forEach((value, key) => {
                    payload[key] = value;
                });

                // UserId와 vendorId 추가
                payload.UserId = vendorId;

                // 필수 파라미터 확인
                if (!payload.AuthResultCode) {
                    throw new Error('인증 결과를 찾을 수 없습니다.');
                }

                // 백엔드 콜백 API 호출
                const response = await post(`${config.api.matching_api}/billing/auth-callback`, payload);

                const result: BillingCallbackResponse = response.data;

                if (result.ok) {
                    setStatus('success');
                    setMessage(result.message || '카드 등록이 완료되었습니다.');
                    setCardInfo({
                        masked: result.cardMasked,
                        name: result.cardName,
                    });
                } else {
                    setStatus('error');
                    setMessage(result.message || '카드 등록에 실패했습니다.');
                }
            } catch (error) {
                setStatus('error');
                setMessage(error instanceof Error ? error.message : '카드 등록 처리 중 오류가 발생했습니다.');
                if (!vendorId) {
                    openLoginDialog(true);
                }
            }
        };

        processReturn();
    }, [searchParams]);

    const handleGoToSubscription = () => {
        navigate('/subscription');
    };

    const handleRetry = () => {
        navigate('/subscription');
    };

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
                <CardContent>
                    {status === 'loading' && (
                        <Box>
                            <CircularProgress size={60} sx={{ mb: 3, color: '#FF4B6B' }} />
                            <Typography variant="h6" gutterBottom>
                                카드 등록 처리 중
                            </Typography>
                            <Typography color="text.secondary">잠시만 기다려주세요...</Typography>
                        </Box>
                    )}

                    {status === 'success' && (
                        <Box>
                            <CheckCircleIcon sx={{ fontSize: 60, color: '#10B981', mb: 3 }} />
                            <Typography variant="h5" gutterBottom color="#10B981">
                                카드 등록 완료
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                {message}
                            </Typography>

                            {cardInfo.masked && (
                                <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                                    <Typography variant="body2">
                                        <strong>등록된 카드:</strong> {cardInfo.name} {cardInfo.masked}
                                    </Typography>
                                </Alert>
                            )}

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGoToSubscription}
                                sx={{
                                    backgroundColor: '#FF4B6B',
                                    '&:hover': { backgroundColor: '#4C4FE0' },
                                    mt: 2,
                                }}
                            >
                                구독 플랜 선택하기
                            </Button>
                        </Box>
                    )}

                    {status === 'error' && (
                        <Box>
                            <ErrorIcon sx={{ fontSize: 60, color: '#EF4444', mb: 3 }} />
                            <Typography variant="h5" gutterBottom color="#EF4444">
                                카드 등록 실패
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                {message}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleRetry}
                                    sx={{ borderColor: '#FF4B6B', color: '#FF4B6B' }}
                                >
                                    다시 시도
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleGoToSubscription}
                                    sx={{
                                        backgroundColor: '#FF4B6B',
                                        '&:hover': { backgroundColor: '#4C4FE0' },
                                    }}
                                >
                                    구독 페이지로 돌아가기
                                </Button>
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
}
